import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Card, Typography, Descriptions, Tag, Table, Alert, Button, Space } from "antd"
import { getProcessedQuoteById } from "../services/QuoteServices"
import { useAuth } from "../contexts/authContext"
import "../assets/styles/QuoteDetails.css"

const { Title, Text } = Typography

const productColumns = [
  { title: "Producto", dataIndex: "name", key: "name" },
  { title: "Cantidad", dataIndex: "quantity", key: "quantity" },
  { title: "Precio unitario", dataIndex: "unit_price", key: "unit_price" },
  { title: "Precio total", dataIndex: "total_price", key: "total_price" },
  { title: "Descripcion", dataIndex: "description", key: "description" },
]

const QuoteDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { getToken } = useAuth()

  const [quote, setQuote] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showRaw, setShowRaw] = useState(false)

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        setLoading(true)
        setError(null)
        const token = await getToken()
        const data = await getProcessedQuoteById(token, id)
        setQuote(data)
      } catch (err) {
        setError(err?.message || "No se pudo cargar el presupuesto")
      } finally {
        setLoading(false)
      }
    }

    fetchQuote()
  }, [id, getToken])

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button onClick={() => navigate(-1)} type="primary">
            Volver
          </Button>
        }
      />
    )
  }

  if (!quote) {
    return (
      <div className="quote-details-page">
        <Card loading title="Detalle de Presupuesto" className="quote-section" style={{ minHeight: 400 }} />
      </div>
    )
  }

  const totalAmount = quote.total_amount ?? quote.total?.amount
  const currency = quote.currency ?? quote.total?.currency ?? "ARS"

  return (
    <div className="quote-details-page">
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div className="quote-header">
          <div>
            <div className="quote-chip">Presupuesto</div>
            <Title level={3} className="quote-title">
              {quote.quote_number || quote.id || "Presupuesto"}
            </Title>
            <Text type="secondary">Proveedor: {quote.seller_name || quote.seller || "No informado"}</Text>
          </div>
          <Button onClick={() => navigate(-1)}>Volver</Button>
        </div>

        <Card title="Resumen" className="quote-section" loading={loading}>
          <Descriptions column={1} size="middle">
            <Descriptions.Item label="Proveedor">
              {quote.seller_name || quote.seller || "-"}{" "}
              {quote.seller_email ? <Text type="secondary">({quote.seller_email})</Text> : null}
            </Descriptions.Item>
        <Descriptions.Item label="Fecha">{quote.quote_date || quote.date || "-"}</Descriptions.Item>
        <Descriptions.Item label="Validez">{quote.validity || "-"}</Descriptions.Item>
        <Descriptions.Item label="Total">
          {totalAmount !== undefined ? `${totalAmount} ${currency}` : "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Condiciones de pago">
          {quote.payment_conditions || "No informadas"}
        </Descriptions.Item>
        <Descriptions.Item label="Entrega/Plazo">
          {quote.delivery_term || quote.deliveryTerm || quote.validity || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Notas">{quote.notes || "Sin notas"}</Descriptions.Item>
        <Descriptions.Item label="Estado">
          <Tag color="blue">{quote.status || "Sin estado"}</Tag>
        </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Productos / Items" className="quote-section" loading={loading}>
          <Table
            dataSource={quote.products || []}
            columns={productColumns}
            rowKey={(item, idx) => `${item.name || "item"}-${idx}`}
            pagination={false}
            size="small"
          />
        </Card>

        {quote.raw_content ? (
          <Card
            title={
              <div
                className="quote-raw-toggle"
                onClick={() => setShowRaw((prev) => !prev)}
                role="button"
                tabIndex={0}
              >
                <span>Contenido original</span>
                <Text type="secondary">{showRaw ? "(ocultar)" : "(ver)"}</Text>
              </div>
            }
            className="quote-section"
            bodyStyle={showRaw ? undefined : { display: "none" }}
          >
            <pre className="quote-raw">{quote.raw_content}</pre>
          </Card>
        ) : null}
      </Space>
    </div>
  )
}

export default QuoteDetails
